'use client';
import { TelegramCategoryTreeResponseDto } from '@/generated';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { IconChevronDown, IconChevronLeft, IconChevronRight, IconChevronUp, IconSearch } from '@tabler/icons-react';
import clsx from 'clsx';
import { usePrevious } from '@/hooks/usePrevious';
import { createPortal } from 'react-dom';
import { inefectiveFindElem } from '@/utils/findTreeElem';
import { useGeoSearchString } from '@/contexts/CookieContext';
import { getLink } from '@/utils/serverSetCategoryLink';
import { getNormalizedNameOfCategoriesPath } from '@/utils/converters/categories';
import { CategorySelectTrigger } from '../../SearchFilters/CategorySelectTrigger';
import { Button } from '@/components/ui/button';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group';
import { FilterSubPanel } from '../FilterSubPanel';
import { ScrollArea } from '@/components/ui/scroll-area';

type CategorySelectProps = {
  data: TelegramCategoryTreeResponseDto[];
  onChange?: (value: string[]) => void;
  value?: string[];
  isMobile?: boolean;
  openedModal?: string;
  showModal?: (id: string) => void;
  closeModal?: (id: string) => void;
};

type CategoryIdDict = {
  [id: string]: TelegramCategoryTreeResponseDto;
};

type TelegramCategoryTreeResponseDtoWithVirtual = TelegramCategoryTreeResponseDto & { isVirtual?: boolean };

const convertTreeToDict = (tree: TelegramCategoryTreeResponseDto[]): CategoryIdDict => {
  const arrToConvert = [...tree];

  const dict: CategoryIdDict = {};

  while (true) {
    if (arrToConvert.length === 0) {
      break;
    }
    const elem = arrToConvert.shift();
    if (!elem) {
      break;
    }
    dict[elem.id] = elem;
    if (elem.children.length > 0) {
      arrToConvert.push(...elem.children);
    }
  }

  return dict;
};

const findDepthToReplace = (dict: CategoryIdDict, selectedIdPath: string[], itemIdToSelect: string) => {
  const elemToSelect = dict[itemIdToSelect];
  if (!elemToSelect.parentId) {
    return {
      depth: selectedIdPath.length,
    };
  }

  const parent = dict[elemToSelect.parentId];
  const childrenIds = parent.children.map((val) => val.id);
  const indexToReplace = selectedIdPath.findIndex((el) => childrenIds.includes(el));

  return {
    depth: indexToReplace === -1 ? 0 : selectedIdPath.length - indexToReplace,
  };
};

export function CategorySelectElem({
  data,
  onChange,
  value: selected = [],
  isMobile = false,
  showModal,
  closeModal,
  openedModal,
}: CategorySelectProps) {
  const t = useTranslations('filters');
  const searchHref = useGeoSearchString();
  const [selectedCategoryList, setSelectedList] = useState<TelegramCategoryTreeResponseDtoWithVirtual[]>(data);
  const validatedData = useRef<boolean>(false);

  const treeListPath = useRef<TelegramCategoryTreeResponseDto[][]>([]);

  const [treeListContainer, setTreeListContainer] = useState<TelegramCategoryTreeResponseDto[][]>([]);

  useEffect(() => {
    // allowed: reading ref in an effect
    setTreeListContainer([...treeListPath.current]);
  }, []);

  const previosData = usePrevious(data);

  const [extended, setExtended] = useState(false);

  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    if (previosData !== null && previosData.length === 0 && data.length !== 0) {
      setSelectedList(data);
    }
  }, [previosData, data]);

  const categoryDict = useMemo(() => {
    return convertTreeToDict(data);
  }, [data]);

  useEffect(() => {
    if (selected.length === 0) {
      treeListPath.current = [];
      setTreeListContainer([...treeListPath.current]);
      setSelectedList(data);
      return;
    }

    if (!selected || selected.length === 0 || data.length === 0 || validatedData.current) {
      return;
    }

    validatedData.current = true;
    //on change selected loading the list of elems
    const { node, path } = inefectiveFindElem(data, selected[selected.length - 1]);
    if (!node || path[path.length - 1]?.children.length === 0) {
      treeListPath.current = [];
      setSelectedList(data);
      setTreeListContainer([...treeListPath.current]);
      return;
    }

    treeListPath.current = [data, ...path.slice(0, path.length - 2).map((val) => val.children)];

    if (Array.isArray(node.children) && node.children.length > 0) {
      setSelectedList(node.children);
      treeListPath.current = [data, ...path.map((val) => val.children)];
      setTreeListContainer([...treeListPath.current]);
      return;
    }

    if (path.length > 0 && path[path.length - 1].children.length > 0) {
      treeListPath.current = [data, ...path.slice(0, path.length).map((val) => val.children)];
      setTreeListContainer([...treeListPath.current]);
      setSelectedList([]);
      return;
    }

    setSelectedList(data);
  }, [selected, data, onChange]);
  const handleChange = useCallback(
    (item: TelegramCategoryTreeResponseDto) => {
      if (
        selectedCategoryList.length === 1 &&
        selectedCategoryList[0] === item &&
        selectedCategoryList[0].isVirtual &&
        selected.includes(item.id)
      ) {
        closeModal?.('category');
        return;
      }

      if (selected.includes(item.id)) {
        if (treeListPath.current[treeListPath.current.length - 1][0].id !== selectedCategoryList[0].id) {
          treeListPath.current.push(selectedCategoryList);
        }
        if (item.children.length !== 0) {
          setSelectedList(item.children);
        } else {
          setSelectedList([]);
        }
        setTreeListContainer([...treeListPath.current]);
        return;
      }

      const { depth } = findDepthToReplace(categoryDict, selected, item.id);
      const newSelected = depth !== 0 ? selected.slice(0, -depth) : [...selected];
      newSelected.push(item.id);

      onChange?.(newSelected);
      treeListPath.current.push(selectedCategoryList);
      setTreeListContainer([...treeListPath.current]);
      if (item.children.length !== 0) {
        setSelectedList(item.children);
      } else {
        setSelectedList([]);
        closeModal?.('category');
      }
    },
    [selectedCategoryList, setSelectedList, onChange, selected, closeModal, categoryDict],
  );

  const goBack = useCallback(() => {
    const lastTree = treeListPath.current.pop();
    if (selected.length) {
      const newSelected = selected.slice(0, selected.length - 1);

      onChange?.(newSelected);
    }

    if (lastTree) {
      setSelectedList(lastTree);
    }
    setTreeListContainer([...treeListPath.current]);
  }, [onChange, selected]);

  const isLong = selectedCategoryList.length > 5;

  useEffect(() => {
    if (isMobile && selectedCategoryList.length === 0) {
      setSelectedList(treeListPath.current[treeListPath.current.length - 1]);
    }
  }, [isMobile, selectedCategoryList]);

  const desktopShownCategory = useMemo(() => {
    if (selected.length > 0) {
      const lastSelected = selected[selected.length - 1];
      const parentId = categoryDict[lastSelected].parentId;
      const parent = categoryDict?.[parentId || ''];
      return parent?.name || t('all_categories');
    }
    return t('all_categories');
  }, [categoryDict, t, selected]);

  const filteredData = useMemo(() => {
    return isLong
      ? selectedCategoryList.filter(
          (val) =>
            val.name.toUpperCase().includes(searchValue.toUpperCase()) ||
            val.id.toUpperCase().includes(searchValue.toUpperCase()),
        )
      : selectedCategoryList;
  }, [selectedCategoryList, searchValue, isLong]);

  const closeCategoryPanel = () => closeModal?.('category');

  const mobileLayout = (
    <FilterSubPanel
      title={t('choosing_category')}
      onBack={treeListContainer.length > 0 ? goBack : closeCategoryPanel}
      toolbar={
        isLong ? (
          <InputGroup className='h-[44px] rounded-[12px] border-primary-stroke-day bg-bg2-day shadow-none'>
            <InputGroupAddon>
              <IconSearch className='size-[20px] text-text-disabled-day' />
            </InputGroupAddon>
            <InputGroupInput value={searchValue} onChange={(e) => setSearchValue(e.target.value)} />
          </InputGroup>
        ) : undefined
      }
      footer={
        <Button onClick={closeCategoryPanel} variant='default' size='full'>
          {t('select_category')}
        </Button>
      }
      bodyClassName='flex flex-col gap-[2px] px-[8px]'
    >
      {selected.length > 0 && (
        <Button
          variant='ghost'
          onClick={closeCategoryPanel}
          className='h-auto min-h-[48px] w-full justify-start rounded-[10px] px-[12px] text-start text-[18px] font-semibold text-text-primary-day hover:bg-bg2-day active:scale-100'
        >
          {categoryDict?.[selected?.[selected.length - 1]]?.name}
        </Button>
      )}
      {filteredData.map((item) => (
        <Button
          variant='ghost'
          onClick={() => {
            handleChange(item);
          }}
          key={item.id}
          className={clsx(
            'h-auto min-h-[48px] w-full justify-between gap-[8px] rounded-[10px] px-[12px] text-start font-normal hover:bg-bg2-day active:scale-100 active:bg-bg2-day',
            {
              'bg-light-green': selected.includes(item.id),
            },
          )}
        >
          <span className='flex min-w-0 items-center gap-[10px]'>
            {item.iconUrl && (
              <Image
                onError={(e: any) => {
                  e.target.style.display = 'none';
                }}
                width={24}
                height={24}
                src={item.iconUrl}
                alt=''
              />
            )}
            <span className='truncate text-m font-normal text-text-primary-day'>{item.name}</span>
          </span>

          {item.children.length > 0 && (
            <IconChevronRight className='size-[20px] shrink-0 text-icon-default rtl:rotate-180' />
          )}
        </Button>
      ))}
    </FilterSubPanel>
  );

  const createOnClick = useCallback(
    (item: TelegramCategoryTreeResponseDtoWithVirtual) => {
      return (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        e.preventDefault();
        e.stopPropagation();
        handleChange(item);
      };
    },
    [handleChange],
  );

  if (isMobile) {
    const target = document.getElementById('mobile-filters-modal-parent');

    const selectedElemPath = selected.map((value) => categoryDict[value].name).join('/');

    return (
      <div className='w-full'>
        {target && openedModal === 'category' && createPortal(mobileLayout, target)}
        <CategorySelectTrigger
          className='w-full'
          onClick={() => {
            showModal?.('category');
          }}
          current={selected.length > 0 ? selectedElemPath : t('choose_category')}
        />
      </div>
    );
  }

  return (
    <>
      <div className='flex flex-col gap-[16px] w-full'>
        {treeListContainer.length > 0 ? (
          <Button
            variant='ghost'
            onClick={goBack}
            className='h-auto w-fit justify-start gap-[4px] p-0 text-[18px] font-semibold text-text-primary-day hover:bg-transparent active:scale-100'
          >
            <IconChevronLeft className='size-[20px] rtl:rotate-180' />
            {desktopShownCategory}
          </Button>
        ) : (
          <div className='text-[18px] font-semibold text-text-primary-day'>{desktopShownCategory}</div>
        )}

        <ScrollArea
          className={clsx('[&>[data-slot=scroll-area-viewport]>div]:block!', {
            'w-full': !isLong,
            '-me-[14px] w-[calc(100%+14px)]': isLong,
            '[&>[data-slot=scroll-area-viewport]]:max-h-[346px]': extended && isLong,
            '[&>[data-slot=scroll-area-viewport]]:max-h-[166px] [&>[data-slot=scroll-area-viewport]]:overflow-hidden! [&>[data-slot=scroll-area-scrollbar]]:hidden':
              !extended && isLong,
          })}
        >
          <div className={clsx('flex flex-col gap-[2px]', isLong && 'pe-[14px]')}>
            {selectedCategoryList.map((item) => (
              <a
                onClick={createOnClick(item)}
                key={item.id}
                href={getLink(getNormalizedNameOfCategoriesPath(item.id, categoryDict), searchHref || '')}
                className='flex max-h-[44px] cursor-pointer items-center justify-between rounded-[8px] ps-[16px] pe-[8px] py-[10px] transition-colors hover:bg-bg2-day'
              >
                <span className='text-text-primary-day text-m font-normal'>{item.name}</span>

                {item.children.length > 0 && (
                  <div className='size-[20px] flex items-center justify-center'>
                    <IconChevronRight className='text-icon-default rtl:rotate-180' size={20} />
                  </div>
                )}
              </a>
            ))}
          </div>
        </ScrollArea>

        {isLong && (
          <Button
            variant='ghost'
            onClick={() => {
              setExtended(!extended);
            }}
            className='h-auto w-auto p-0 ms-[16px] gap-[4px] text-green-day text-[16px] mt-[12px] hover:bg-transparent'
          >
            <span>{extended ? t('hide') : t('showAll')}</span>
            {extended ? (
              <IconChevronUp className='justify-self-end text-green-day' size={16} />
            ) : (
              <IconChevronDown className='justify-self-end text-green-day' size={16} />
            )}
          </Button>
        )}
      </div>
    </>
  );
}
